import {getAiLobbyCountdownState} from '../../utils/aiMatch/getAiLobbyCountdownState';

// Больше игроков лобби не вмещает (тот же предел у сервера и у десктопного
// лобби, см. LobbyDesktop)
export const LOBBY_LIST_MAX_PLAYERS = 6;

// Этап лобби для его карточки в списке
export const LOBBY_LIST_PHASES = {
  GATHERING: 'gathering',
  BANS: 'bans',
  FACTION_SELECT: 'faction_select',
  STARTED: 'started',
};

/**
 * Этап лобби по его дескриптору из списка: матч идет, баны фракций, выбор
 * фракций рейтингового матча (после банов, до автостарта) или сбор игроков
 *
 * @param {Object} lobby
 * @returns {string} LOBBY_LIST_PHASES
 */
export const getLobbyListPhase = (lobby) => {
  if (lobby.status === 'game_started') {
    return LOBBY_LIST_PHASES.STARTED;
  }
  if (lobby.factionBanDeadlineAt) {
    return LOBBY_LIST_PHASES.BANS;
  }
  if (lobby.isRanked && lobby.rankMatchStartAt) {
    return LOBBY_LIST_PHASES.FACTION_SELECT;
  }
  return LOBBY_LIST_PHASES.GATHERING;
};

const getSecondsLeft = (deadlineAt, now) => Math.max(0, Math.ceil((deadlineAt - now) / 1000));

/**
 * Подпись этапа на карточке. У бана и выбора фракций — остаток секунд (по
 * часам сервера: now уже с поправкой), у сбора ИИ-лобби — отсчет события, как
 * на кнопке меню. У обычного сбора игроков подписи нет: карточка и так в
 * разделе «Сбор игроков»
 *
 * @param {Object} lobby
 * @param {number} now - время сервера, мс
 * @returns {string|null}
 */
export const getLobbyListStatusText = (lobby, now) => {
  switch (getLobbyListPhase(lobby)) {
    case LOBBY_LIST_PHASES.STARTED:
      return 'Матч идет';
    case LOBBY_LIST_PHASES.BANS:
      return `Баны фракций · ${getSecondsLeft(lobby.factionBanDeadlineAt, now)} с`;
    case LOBBY_LIST_PHASES.FACTION_SELECT:
      return `Выбор фракций · ${getSecondsLeft(lobby.rankMatchStartAt, now)} с`;
    default: {
      const aiCountdown = getAiLobbyCountdownState({lobby, now});
      return aiCountdown ? `${aiCountdown.prefix} ${aiCountdown.timeText}` : null;
    }
  }
};

/**
 * Идет ли в карточке живой отсчет — тогда список перерисовывается раз в
 * секунду
 *
 * @param {Object} lobby
 * @returns {boolean}
 */
export const checkIfLobbyListStatusTicking = (lobby) => {
  const phase = getLobbyListPhase(lobby);
  return phase === LOBBY_LIST_PHASES.BANS
    || phase === LOBBY_LIST_PHASES.FACTION_SELECT
    || (phase === LOBBY_LIST_PHASES.GATHERING && Boolean(lobby.isAiMatch));
};

/**
 * Рейтинговое лобби, в которое из списка не войти: его состав собирает сервер
 * — матч-мейкинг (у такого лобби нет создателя, и баны в нем открываются
 * сразу при сборке) или витрина ботов. Ручное рейтинговое лобби админа (есть
 * создатель) и ИИ-лобби до банов открыты, как обычные (см.
 * checkIfRankMatchJoinBlocked)
 *
 * @param {Object} lobby
 * @returns {boolean}
 */
const checkIfServerAssembledRankedLobby = (lobby) => Boolean(lobby.isRanked) && !lobby.isAiMatch
  && (!lobby.creatorId || Boolean(lobby.isBotsOnlyMatch));

/**
 * Открыт ли состав лобби: войти еще можно, пока идет сбор игроков и состав не
 * собирает сам сервер (матч-мейкинг, витрина ботов)
 *
 * @param {Object} lobby
 * @returns {boolean}
 */
export const checkIfLobbyListSeatsOpen = (lobby) => !checkIfServerAssembledRankedLobby(lobby)
  && getLobbyListPhase(lobby) === LOBBY_LIST_PHASES.GATHERING;

/**
 * Вместимость лобби: у ИИ-лобби — своя, из расписания промо-матча
 *
 * @param {Object} lobby
 * @returns {number}
 */
export const getLobbyListMaxPlayers = (lobby) => lobby.aiPromo?.maxPlayers || LOBBY_LIST_MAX_PLAYERS;

/**
 * Сколько мест показать в карточке лобби в сборе: в открытом составе —
 * свободные тоже (пустыми), в закрытом — ровно по игрокам
 *
 * @param {Object} lobby
 * @returns {number}
 */
export const getLobbyListSeatsCount = (lobby) => {
  const playersCount = (lobby.players || []).length;
  return checkIfLobbyListSeatsOpen(lobby)
    ? Math.max(playersCount, getLobbyListMaxPlayers(lobby))
    : playersCount;
};
